import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { usePolicy } from './PolicyContext';
import {
    Notification,
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markAllNotificationsRead,
} from '../services/notificationService';
import { createLogger } from '../utils/logger';

const logger = createLogger('NotificationContext');

const PAGE_SIZE = 20;

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    loadNotifications: () => Promise<void>;
    loadMoreNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    refreshUnreadCount: () => Promise<void>;
    pollIfStale: (minIntervalMs: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
    return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token, currentUser } = useAuth();
    const { policiesAccepted } = usePolicy();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const currentPage = useRef(1);
    const totalRef = useRef(0);
    const lastPolledAt = useRef<number>(0);
    const isPolling = useRef(false);

    const loadUnreadCount = useCallback(async () => {
        if (!token || policiesAccepted !== true) return;
        try {
            const count = await fetchUnreadCount(token);
            setUnreadCount(count);
            lastPolledAt.current = Date.now();
        } catch (err) {
            logger.error('Failed to fetch unread count', err);
        }
    }, [token, policiesAccepted]);

    const refreshUnreadCount = loadUnreadCount;

    const pollIfStale = useCallback(
        async (minIntervalMs: number) => {
            if (isPolling.current) return;
            if (Date.now() - lastPolledAt.current < minIntervalMs) return;
            isPolling.current = true;
            try {
                await loadUnreadCount();
            } finally {
                isPolling.current = false;
            }
        },
        [loadUnreadCount]
    );

    // Loads page 1 fresh — called when dropdown opens
    const loadNotifications = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await fetchNotifications(token, 1, PAGE_SIZE);
            setNotifications(data.items);
            currentPage.current = 1;
            totalRef.current = data.total;
            setHasMore(data.items.length < data.total);
        } catch (err) {
            logger.error('Failed to fetch notifications', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Appends the next page — called on scroll-to-bottom
    const loadMoreNotifications = useCallback(async () => {
        if (!token || loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = currentPage.current + 1;
            const data = await fetchNotifications(token, nextPage, PAGE_SIZE);
            setNotifications((prev) => {
                const existingIds = new Set(prev.map((n) => n.id));
                const newItems = data.items.filter((n) => !existingIds.has(n.id));
                return [...prev, ...newItems];
            });
            currentPage.current = nextPage;
            totalRef.current = data.total;
            setHasMore((currentPage.current * PAGE_SIZE) < data.total);
        } catch (err) {
            logger.error('Failed to load more notifications', err);
        } finally {
            setLoadingMore(false);
        }
    }, [token, loadingMore, hasMore]);

    const markAsRead = useCallback(
        async (id: string) => {
            if (!token) return;
            try {
                const updated = await markNotificationRead(token, id);
                setNotifications((prev) =>
                    prev.map((n) => (n.id === id ? updated : n))
                );
                setUnreadCount((prev) => Math.max(0, prev - 1));
            } catch (err) {
                logger.error('Failed to mark notification as read', err);
            }
        },
        [token]
    );

    const markAllAsRead = useCallback(async () => {
        if (!token) return;
        try {
            await markAllNotificationsRead(token);
            setNotifications((prev) =>
                prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() }))
            );
            setUnreadCount(0);
        } catch (err) {
            logger.error('Failed to mark all notifications as read', err);
        }
    }, [token]);

    // Load unread count on login — never derives count from loaded items.
    // Gated on policiesAccepted so we don't fire while the policy-acceptance modal is blocking the app.
    useEffect(() => {
        if (currentUser && token && policiesAccepted === true) {
            loadUnreadCount();
        }
    }, [currentUser, token, policiesAccepted, loadUnreadCount]);

    const contextValue = useMemo(
        () => ({
            notifications,
            unreadCount,
            loading,
            loadingMore,
            hasMore,
            loadNotifications,
            loadMoreNotifications,
            markAsRead,
            markAllAsRead,
            refreshUnreadCount,
            pollIfStale,
        }),
        [notifications, unreadCount, loading, loadingMore, hasMore, loadNotifications, loadMoreNotifications, markAsRead, markAllAsRead, refreshUnreadCount, pollIfStale]
    );

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
        </NotificationContext.Provider>
    );
};
